//*Libraries imports
import { useEffect, useState } from "react";
import { X, Loader2 } from "lucide-react";
import * as XLSX from "xlsx";

type SpreadsheetViewerProps = {
  url: string;
  onClose: () => void;
  title?: string;
};

export function SpreadsheetViewer({
  url,
  onClose,
  title = "Visualizador de Planilha",
}: SpreadsheetViewerProps) {
  const [data, setData] = useState<any[]>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [sheets, setSheets] = useState<string[]>([]);
  const [currentSheet, setCurrentSheet] = useState<number>(0);
  const [workbook, setWorkbook] = useState<XLSX.WorkBook | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSpreadsheet = async () => {
      try {
        setLoading(true);

        let fetchUrl = url;

        const s3Domain =
          "https://br-coverforce-executor.s3.sa-east-1.amazonaws.com"; //! todo: Ajustar isso depois

        if (
          window.location.hostname === "localhost" &&
          url.startsWith(s3Domain)
        ) {
          fetchUrl = url.replace(s3Domain, "/s3-proxy");
        }

        const response = await fetch(fetchUrl);
        if (!response.ok) {
          throw new Error("Falha ao carregar o arquivo");
        }
        const arrayBuffer = await response.arrayBuffer();
        const wb = XLSX.read(arrayBuffer, { type: "array", cellDates: true });

        setWorkbook(wb);
        setSheets(wb.SheetNames);

        if (wb.SheetNames.length > 0) {
          loadSheet(wb, 0);
        }
      } catch (err) {
        console.error("Erro ao ler planilha:", err);
        setError("Não foi possível carregar a planilha.");
      } finally {
        setLoading(false);
      }
    };

    if (url) {
      fetchSpreadsheet();
    }
  }, [url]);

  const loadSheet = (wb: XLSX.WorkBook, index: number) => {
    const sheetName = wb.SheetNames[index];
    const worksheet = wb.Sheets[sheetName];
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

    if (jsonData.length > 0) {
      const cols = jsonData[0] as string[];
      const rows = jsonData.slice(1);
      setColumns(cols);
      setData(rows);
    } else {
      setColumns([]);
      setData([]);
    }
    setCurrentSheet(index);
  };

  const handleSheetChange = (index: number) => {
    if (workbook) {
      loadSheet(workbook, index);
    }
  };

  return (
    <div className="flex flex-col size-full min-h-[80vh] bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-200">
      <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gray-50/50">
        <div className="flex flex-col gap-1">
          <h2 className="text-sm font-semibold text-gray-700">{title}</h2>
          {sheets.length > 1 && (
            <div className="flex gap-2 mt-1">
              {sheets.map((sheet, index) => (
                <button
                  key={sheet}
                  onClick={() => handleSheetChange(index)}
                  className={`px-2 py-0.5 text-xs rounded-md transition-colors ${
                    currentSheet === index
                      ? "bg-blue-100 text-blue-700 font-medium"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {sheet}
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          onClick={onClose}
          className="p-1.5 hover:bg-gray-200 rounded-full transition-colors text-gray-500"
          title="Fechar visualizador"
        >
          <X size={18} />
        </button>
      </div>
      <div className="flex-1 bg-gray-50 p-4 overflow-auto">
        {loading ? (
          <div className="flex justify-center items-center h-full">
            <Loader2 className="animate-spin text-blue-500" size={32} />
          </div>
        ) : error ? (
          <div className="flex justify-center items-center h-full text-red-500">
            {error}
          </div>
        ) : (
          <div className="border border-gray-200 rounded-lg overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {columns.map((col, index) => (
                    <th
                      key={index}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {col || `Coluna ${index + 1}`}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.map((row: any, rowIndex) => (
                  <tr key={rowIndex} className="hover:bg-gray-50">
                    {columns.map((_, colIndex) => (
                      <td
                        key={colIndex}
                        className="px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                      >
                        {row[colIndex] !== undefined
                          ? row[colIndex] instanceof Date
                            ? row[colIndex].toLocaleDateString()
                            : String(row[colIndex])
                          : ""}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {data.length === 0 && (
              <div className="p-4 text-center text-gray-500">
                Planilha vazia
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
